import { useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import KpiConstraintInput from "./KpiConstraintInput";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { SYMBOLS } from "utils/symbols";

const AVAILABLE_INDICATORS = [
  "MA (均線)", "EMA (指數均線)", "MACD (平滑異同移動平均線)",
  "RSI (相對強弱指標)", "ATR (真實波動幅度)", "Bollinger Bands (布林通道)",
  "Stochastic (KD 隨機指標)", "ADX (平均趨向指數)"
];

// 商品池統一常數從 utils/symbols.js import（共 4 個檔案共用）

function BeastForm({ onSubmit }) {
  const [params, setParams] = useState({
    symbol: "XAUUSD",
    frequency: "D1",
    startYear: 2020,
    endYear: 2024,
    capital: 10000,
    tradeDirection: "AUTO",
    maxIterate: 10,
    kpis: {
      returnRatePct: { enabled: true, type: "range", min: 30, max: 80 },
      maxDrawdownPct: { enabled: true, type: "lt", value: 40 },
      trades: { enabled: true, type: "range", min: 10, max: 200 },
      profitLossRatio: { enabled: false, type: "gt", value: 2 },
      profitAmount: { enabled: false, type: "gt", value: 5000 },
      winRate: { enabled: false, type: "gt", value: 60 },
    },
    indicators: ["MA (均線)", "RSI (相對強弱指標)", "MACD (平滑異同移動平均線)"], // 預設勾選幾個
  });

  const handleChange = (field) => (event) => {
    setParams({ ...params, [field]: event.target.value });
  };

  const handleKpiChange = (kpiName) => (newValue) => {
    // 報酬率不可被關閉
    if (kpiName === "returnRatePct" && newValue.enabled === false) {
      newValue.enabled = true;
    }
    setParams({
      ...params,
      kpis: { ...params.kpis, [kpiName]: newValue },
    });
  };

  const handleIndicatorToggle = (indicator) => {
    const currentIndex = params.indicators.indexOf(indicator);
    const newIndicators = [...params.indicators];

    if (currentIndex === -1) {
      newIndicators.push(indicator);
    } else {
      newIndicators.splice(currentIndex, 1);
    }

    setParams({ ...params, indicators: newIndicators });
  };

  const handleSubmit = () => {
    // 驗證參數
    const enabledKpis = Object.entries(params.kpis).filter(([_, config]) => config.enabled);
    
    for (const [kpiName, config] of enabledKpis) {
      if (config.type === "range") {
        if (!config.min || !config.max) {
          alert(`${kpiName} 的區間值不完整`);
          return;
        }
        if (config.min >= config.max) {
          alert(`${kpiName} 的最小值必須小於最大值`);
          return;
        }
      } else {
        if (!config.value) {
          alert(`${kpiName} 的值不能為空`);
          return;
        }
      }
    }

    // 轉換為 API 格式
    const apiParams = {
      symbol: params.symbol,
      frequency: params.frequency,
      startYear: parseInt(params.startYear),
      endYear: parseInt(params.endYear),
      capital: parseFloat(params.capital),
      tradeDirection: params.tradeDirection,
      kpiTargets: params.kpis,
      indicators: params.indicators,
      maxIterate: parseInt(params.maxIterate),
      fee: 0.0006,
      slippage: 0.0003,
    };

    onSubmit(apiParams);
  };

  return (
    <MDBox>
      <MDTypography variant="h6" mb={2}>
        策略參數設定
      </MDTypography>

      <Grid container spacing={3}>
        {/* 商品設定 */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="交易商品"
            value={params.symbol}
            onChange={handleChange("symbol")}
          >
            {SYMBOLS.map((s) => (
              <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="時間週期"
            value={params.frequency}
            onChange={handleChange("frequency")}
          >
            <MenuItem value="M1">1分鐘 (M1)</MenuItem>
            <MenuItem value="M5">5分鐘 (M5)</MenuItem>
            <MenuItem value="M15">15分鐘 (M15)</MenuItem>
            <MenuItem value="M30">30分鐘 (M30)</MenuItem>
            <MenuItem value="H1">1小時 (H1)</MenuItem>
            <MenuItem value="H4">4小時 (H4)</MenuItem>
            <MenuItem value="H12">12小時 (H12)</MenuItem>
            <MenuItem value="D1">日線 (D1)</MenuItem>
          </TextField>
        </Grid>

        {/* 交易方向 */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="交易方向"
            value={params.tradeDirection}
            onChange={handleChange("tradeDirection")}
            helperText="選擇策略的交易方向，或讓 AI 自行判斷"
          >
            <MenuItem value="AUTO">不選擇（AI 自行判斷）</MenuItem>
            <MenuItem value="LONG">僅做多</MenuItem>
            <MenuItem value="SHORT">僅做空</MenuItem>
          </TextField>
        </Grid>

        {/* 回測期間 */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="數據回測起始年份"
            value={params.startYear}
            onChange={handleChange("startYear")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="數據回測結束年份"
            value={params.endYear}
            onChange={handleChange("endYear")}
          />
        </Grid>

        {/* 初始資金 */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="初始資金 (USD)"
            value={params.capital}
            onChange={handleChange("capital")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="迭代次數"
            value={params.maxIterate}
            onChange={handleChange("maxIterate")}
          />
        </Grid>

        <Grid item xs={12}>
          <MDTypography variant="h6" mt={3} mb={1}>
            技術指標池 (Indicator Pool)
          </MDTypography>
          <MDTypography variant="caption" color="text" mb={2} display="block">
            勾選您希望 AI 參考的指標。AI 將從中挑選 1~3 個進行搭配 (OR 邏輯)，不會強制全部使用。
          </MDTypography>
          <FormGroup row>
            {AVAILABLE_INDICATORS.map((ind) => (
              <FormControlLabel
                key={ind}
                control={
                  <Checkbox
                    checked={params.indicators.indexOf(ind) !== -1}
                    onChange={() => handleIndicatorToggle(ind)}
                  />
                }
                label={<MDTypography variant="body2" color="text">{ind}</MDTypography>}
                sx={{ width: { xs: "100%", sm: "50%", md: "33%" }, margin: 0 }}
              />
            ))}
          </FormGroup>
        </Grid>

        {/* KPI 目標 */}
        <Grid item xs={12}>
          <MDTypography variant="h6" mt={3} mb={1}>
            KPI 目標設定
          </MDTypography>
          <MDTypography variant="caption" color="text" mb={2} display="block">
            <b>報酬率為必設項目。</b>其餘 KPI 目標將作為加分項，AI 會盡量達成，但不會因為單一次要條件未達標就全盤否定策略。
          </MDTypography>
        </Grid>

        <Grid item xs={12}>
          <KpiConstraintInput
            label="報酬率"
            value={params.kpis.returnRatePct}
            onChange={handleKpiChange("returnRatePct")}
            unit="%"
          />
        </Grid>

        <Grid item xs={12}>
          <KpiConstraintInput
            label="最大回撤"
            value={params.kpis.maxDrawdownPct}
            onChange={handleKpiChange("maxDrawdownPct")}
            unit="%"
          />
        </Grid>

        <Grid item xs={12}>
          <KpiConstraintInput
            label="交易次數"
            value={params.kpis.trades}
            onChange={handleKpiChange("trades")}
            unit="次"
          />
        </Grid>

        <Grid item xs={12}>
          <KpiConstraintInput
            label="賺賠比"
            value={params.kpis.profitLossRatio}
            onChange={handleKpiChange("profitLossRatio")}
            unit=""
          />
        </Grid>

        <Grid item xs={12}>
          <KpiConstraintInput
            label="獲利金額"
            value={params.kpis.profitAmount}
            onChange={handleKpiChange("profitAmount")}
            unit="USD"
          />
        </Grid>

        <Grid item xs={12}>
          <KpiConstraintInput
            label="勝率"
            value={params.kpis.winRate}
            onChange={handleKpiChange("winRate")}
            unit="%"
          />
        </Grid>

        {/* 提交按鈕 */}
        <Grid item xs={12}>
          <MDBox mt={3} display="flex" justifyContent="center">
            <MDButton variant="gradient" color="info" onClick={handleSubmit} size="large">
              開始生成策略
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default BeastForm;
